import { useAuth } from "@clerk/clerk-react";
import { Box, CircularProgress } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DefaultVaOnboardingStepInfo,
  VAStatus,
  VaOnboardingStep,
  getDefaultVaOnboardingSteps,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { Pill } from "features/Influencer/HomePage/Gamification/components/shared/primitives";
import HomeCard from "features/Influencer/HomePage/components/HomeCard";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const SparkleIcon = () => (
  <svg width={20} height={20} {...svgProps}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6 7.7 7.7M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </svg>
);

const CalendarIcon = () => (
  <svg width={11} height={11} {...svgProps}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CheckIcon = () => (
  <svg width={13} height={13} {...svgProps}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width={14} height={14} {...svgProps}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const OnboardingProgress = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [resumeLoading, setResumeLoading] = useState(false);
  const navigate = useNavigate();

  const { organizationVaProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const { hasPurchasedAccountReview, canSubmitReview } =
    useAccountReviewState();
  const defaultSteps = getDefaultVaOnboardingSteps(canSubmitReview);

  const isComplete = (index: number) => {
    if (isInMiddleOfDifferentOnboarding()) return false;
    return (
      index < (organizationVaProfile?.onboardingState?.currentStepIndex || 0)
    );
  };

  const calculatePercentageOfCompletion = () => {
    const currentStepIndex =
      organizationVaProfile?.onboardingState?.currentStepIndex || 0;
    const totalSteps = defaultSteps.length;
    return Math.round((currentStepIndex / totalSteps) * 100);
  };

  // Edge case: user in the middle of an old onboarding flow but never complete
  const isInMiddleOfDifferentOnboarding = () => {
    if (!organizationVaProfile?.onboardingState) return false;
    return !lodash.isEqual(
      organizationVaProfile.onboardingState.steps,
      defaultSteps,
    );
  };

  const handleProceed = async () => {
    setResumeLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-status`,
        "PATCH",
        {},
        {
          status: VAStatus.pending,
        },
      );
      navigate(`/${routes.onboardingVaSelfServe}`);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setResumeLoading(false);
    }
  };

  const percent = calculatePercentageOfCompletion();
  const differentOnboarding = isInMiddleOfDifferentOnboarding();

  return (
    <HomeCard
      sx={styles.card}
      icon={<SparkleIcon />}
      iconSx={styles.iconBox}
      title={
        differentOnboarding
          ? "Resume your onboarding"
          : "Complete your onboarding"
      }
      subtitle={
        <>
          <Box component="strong" sx={styles.subStrong}>
            {differentOnboarding
              ? "👋 We've updated our onboarding since your last login."
              : `You're ${percent}% set up.`}
          </Box>{" "}
          {differentOnboarding
            ? "Don't worry — we still have your old information. Pick up where you left off."
            : "Finish setting up your Virtual Assistant onboarding to start sending personalized brand pitches."}
        </>
      }
      chip={<Pill sx={styles.badge}>Setup · {percent}%</Pill>}
    >
      <Box sx={styles.progressBar}>
        <Box sx={styles.progressFill(percent)} />
      </Box>

      <Box sx={styles.steps}>
        {defaultSteps?.map((step: VaOnboardingStep, index) =>
          step in DefaultVaOnboardingStepInfo ? (
            <Box component="span" key={step} sx={styles.step}>
              {DefaultVaOnboardingStepInfo[step]?.name}
              {isComplete(index) ? (
                <Box component="span" sx={styles.stepTime}>
                  <CheckIcon />
                </Box>
              ) : (
                <Box component="span" sx={styles.stepTime}>
                  <CalendarIcon /> {DefaultVaOnboardingStepInfo[step]?.time} min
                </Box>
              )}
            </Box>
          ) : null,
        )}
        {hasPurchasedAccountReview && (
          <Box component="span" sx={styles.step}>
            Setup Account
            <Box component="span" sx={styles.stepTime}>
              <CalendarIcon /> 1 min
            </Box>
          </Box>
        )}
      </Box>

      <Box
        component="button"
        sx={styles.cta}
        onClick={handleProceed}
        disabled={resumeLoading}
      >
        {resumeLoading ? (
          <CircularProgress size={16} sx={{ color: "#fff" }} />
        ) : (
          <>
            Complete onboarding setup <ArrowRightIcon />
          </>
        )}
      </Box>
    </HomeCard>
  );
};

export default OnboardingProgress;
